import {
  type CatalogFilters,
  type CatalogSort,
  type Filterable,
  labelRefs,
  matchesFilters,
  type Offered,
} from '@/entities/catalog';
import type { AccessibleBoard } from '@/entities/collab';
import type { Database } from '@/entities/database';
import { readRecentDocumentIds, type WorkspaceDocument } from '@/entities/document';

import type { HomePlaces } from '@/entities/places';

/** What the home dashboard lets the viewer filter by: everything, including the workspace kind. */
export const HOME_OFFERED: Offered = {
  type: true,
  favorites: true,
  recent: true,
  owner: true,
  space: true,
  label: true,
};

/** The orders of the home: boards (and databases by name); the manual order is a workspace's. */
export const HOME_SORTS: readonly CatalogSort[] = ['opened', 'updated', 'name'];

/** How a board is seen by the shared filters and search (name, workspace and label names). */
export const boardFilterable = (
  board: AccessibleBoard,
  opened: ReadonlySet<string>,
): Filterable => ({
  kind: 'board',
  favorite: board.is_favorite,
  recent: opened.has(board.id),
  ownerId: board.owner_user_id,
  space: board.workspace_type === 'personal' ? 'personal' : 'team',
  haystack: [
    board.name.toLocaleLowerCase(),
    (board.workspace_name ?? '').toLocaleLowerCase(),
    ...board.labels.map((label) => label.name.toLocaleLowerCase()),
  ],
  labels: labelRefs(board.labels),
});

/** How a database is seen by the shared filters and search (name and workspace name). */
export const databaseFilterable = (
  database: Database,
  opened: ReadonlySet<string>,
  spaceOf: ReadonlyMap<string, 'personal' | 'team'>,
  workspaceNameOf: ReadonlyMap<string, string>,
): Filterable => ({
  kind: 'database',
  favorite: database.is_favorite,
  recent: opened.has(database.id),
  ownerId: database.owner_user_id,
  space: spaceOf.get(database.workspace_id) ?? 'team',
  haystack: [
    database.name.toLocaleLowerCase(),
    (workspaceNameOf.get(database.workspace_id) ?? '').toLocaleLowerCase(),
    ...database.labels.map((label) => label.name.toLocaleLowerCase()),
  ],
  labels: labelRefs(database.labels),
});

/** The catalog address, carrying the search text when there is one (the catalogs read `?q=`). */
export const catalogLink = (path: string, query: string): string =>
  query.trim() === '' ? path : `${path}?q=${encodeURIComponent(query.trim())}`;

/** How a note is seen by the shared filters and search (name, workspace and where it is filed). */
export const documentFilterable = (
  document: WorkspaceDocument,
  opened: ReadonlySet<string>,
  spaceOf: ReadonlyMap<string, 'personal' | 'team'>,
  place: string,
): Filterable => ({
  kind: 'document',
  favorite: document.is_favorite,
  recent: opened.has(document.id),
  ownerId: document.owner_user_id,
  space: spaceOf.get(document.workspace_id) ?? 'team',
  haystack: [
    document.name.toLocaleLowerCase(),
    place.toLocaleLowerCase(),
    ...(document.labels ?? []).map((label) => label.name.toLocaleLowerCase()),
  ],
  labels: labelRefs(document.labels),
});

/** The notes the viewer can open, in every workspace and at any depth of groups, listed ONCE. */
export function documentsMatching(
  documents: readonly WorkspaceDocument[],
  places: HomePlaces,
  filters: CatalogFilters,
  userId: string | null,
  query: string,
  sort: CatalogSort,
  locale: string,
) {
  const order = userId ? readRecentDocumentIds(userId) : [];
  const opened = new Set(order);
  const list = documents.filter((document) =>
    matchesFilters(
      documentFilterable(document, opened, places.spaces, places.locationOf(document)),
      filters,
      userId,
      query,
      HOME_OFFERED,
    ),
  );
  if (sort === 'name') {
    return list.toSorted(
      (a, b) => a.name.localeCompare(b.name, locale) || a.id.localeCompare(b.id),
    );
  }
  if (sort === 'opened') {
    const rank = (id: string) => order.indexOf(id) + 1 || Number.MAX_SAFE_INTEGER;
    return list.toSorted((a, b) => rank(a.id) - rank(b.id));
  }
  return list.toSorted((a, b) => b.updated_at.localeCompare(a.updated_at));
}
